/**
 * Tile manager: which precision and prefixes the viewport needs (SPEC §9.3),
 * what is already cached, and one fetch per missing tile.
 */
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { MapResponse, Provider } from '@latency-globe/shared';
import { getMap } from './api.ts';
import { LRU, planView } from './lod.ts';
import type { CellInfo } from './store.ts';

export function decodeTile(t: MapResponse): CellInfo[] {
  const out: CellInfo[] = [];
  for (let i = 0; i < t.cells.length; i++) {
    out.push({
      cell: t.prefix + t.cells[i]!,
      code: t.codes[t.w[i]!]!,
      score: t.s[i]!,
      total: t.t[i]!,
      runnerUp: t.r[i]! >= 0 ? t.codes[t.r[i]!]! : null,
      boot: t.b[i]!,
    });
  }
  return out;
}

export class View {
  private tiles = new LRU<CellInfo[]>(200);
  private inflight = new Map<string, Promise<CellInfo[]>>();
  private precision = 2;
  private generation = 0;
  maxPrecision = 5;

  constructor(private map: MapLibreMap, private onData: (cells: CellInfo[], precision: number) => void, private onError: (e: unknown) => void) {}

  get currentPrecision(): number { return this.precision; }

  private key(provider: Provider, build: number, precision: number, prefix: string): string {
    return `${provider}:${build}:${precision}:${prefix}`;
  }

  private tile(provider: Provider, build: number, precision: number, prefix: string): Promise<CellInfo[]> {
    const k = this.key(provider, build, precision, prefix);
    const hit = this.tiles.get(k);
    if (hit) return Promise.resolve(hit);
    let p = this.inflight.get(k);
    if (!p) {
      p = getMap(provider, precision, prefix, build).then(decodeTile).then((cells) => { this.tiles.set(k, cells); this.inflight.delete(k); return cells; })
        .catch((e) => { this.inflight.delete(k); throw e; });
      this.inflight.set(k, p);
    }
    return p;
  }

  /** Refresh for the current viewport. Later calls supersede earlier ones. */
  async refresh(provider: Provider, build: number): Promise<void> {
    const gen = ++this.generation;
    const b = this.map.getBounds();
    const bounds = { minLat: Math.max(-85, b.getSouth()), maxLat: Math.min(85, b.getNorth()), minLon: b.getWest(), maxLon: b.getEast() };
    if (bounds.maxLon - bounds.minLon >= 360) { bounds.minLon = -180; bounds.maxLon = 180; }
    else { bounds.minLon = wrap(bounds.minLon); bounds.maxLon = wrap(bounds.maxLon); }
    const { precision, prefixes } = planView(bounds, this.map.getZoom(), this.precision, this.maxPrecision);
    this.precision = precision;
    try {
      const tiles = await Promise.all(prefixes.map((p) => this.tile(provider, build, precision, p)));
      if (gen !== this.generation) return;
      this.onData(tiles.flat(), precision);
    } catch (e) {
      if (gen === this.generation) this.onError(e);
    }
  }
}

function wrap(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}
